import {
  H1_DIMENSIONS,
  H1_LINE_HEIGHTS,
  H2_DIMENSIONS,
  H2_LINE_HEIGHTS,
  H3_DIMENSIONS,
  H3_LINE_HEIGHTS,
  H4_DIMENSIONS,
  H4_LINE_HEIGHTS,
  H5_DIMENSIONS,
  H5_LINE_HEIGHTS,
  H6_DIMENSIONS,
  H6_LINE_HEIGHTS,
  IFontSizes,
  IFontWeightName,
  P_LARGE_DIMENSIONS,
  P_LARGE_LINE_HEIGHTS,
  P_LINE_HEIGHTS,
  P_SIZES,
} from "./fonts";
import {
  DESKTOP_SIZE_BREAKPOINT_MIN,
  IScreenDimensions,
  MOBILE_SIZE_BREAKPOINT_MAX,
  Spacing,
  TABLET_SIZE_BREAKPOINT_MAX,
  TABLET_SIZE_BREAKPOINT_MIN,
} from "./sizes";

interface Screen {
  breakpoint: {
    min?: number;
    max?: number;
  };
}

interface IColorsSet {
  default?: string;
  inverted?: string;
  disabled?: string;
  visited?: string;
  hover?: string;
  active?: string;
  error?: string;
}

interface Padding {
  vertical: number;
  horizontal: number;
}

interface Font {
  size: number;
  weight: IFontWeightName;
  height: number;
  color?: string;
}

interface ScaledSize {
  width: number;
  height: number;
  scale: number;
  fontScale: number;
}

interface IThemeButtonSize {
  padding: Padding;
  font: Font;
  radius: number;
  chip: {
    padding: Padding;
    radius: number;
  };
}

interface IThemeButtonRole {
  colors: IColorsSet;
}

interface ITheme {
  sizes: {
    baseline: number;
    spacing: Spacing;
    desktop: Screen;
    tablet: Screen;
    mobile: Screen;
  };
  fonts: {
    semantics: {
      h1: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      h2: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      h3: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      h4: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      h5: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      h6: {
        sizes: IScreenDimensions;
        heights: IScreenDimensions;
      };
      p: {
        sizes: IFontSizes;
        heights: IFontSizes;
      };
      span: {
        sizes: IFontSizes;
        heights: IFontSizes;
      };
    };
    roles: {
      primary: {
        colors: IColorsSet;
      };
      secondary: {
        colors: IColorsSet;
      };
      tertiary: {
        colors: IColorsSet;
      };
      inverted: {
        colors: IColorsSet;
      };
      base: {
        colors: IColorsSet;
      };
    };
  };
  buttons: {
    sizes: {
      large: IThemeButtonSize;
      semi_large: IThemeButtonSize;
      default: IThemeButtonSize;
      medium: IThemeButtonSize;
      small: IThemeButtonSize;
    };
    roles: {
      primary: IThemeButtonRole;
      secondary: IThemeButtonRole;
      neutral: IThemeButtonRole;
      tertiary: IThemeButtonRole;
    };
  };
  links: {
    sizes: {
      large: {
        font: Font;
      };
      default: {
        font: Font;
      };
      small: {
        font: Font;
      };
      extra_small: {
        font: Font;
      };
    };
    roles: {
      primary: {
        colors: IColorsSet;
      };
      secondary: {
        colors: IColorsSet;
      };
      neutral: {
        colors: IColorsSet;
      };
      tertiary: {
        colors: IColorsSet;
      };
    };
  };
  slider: {
    text: Font;
    colors: {
      text: IColorsSet;
      content: IColorsSet;
    };
  };
  inputs: {
    sizes: {
      large: {
        label: Font;
        text: Font;
        description: Font;
        radius: number;
        height: number;
      };
      semi_large: {
        label: Font;
        text: Font;
        description: Font;
        radius: number;
        height: number;
      };
      default: {
        label: Font;
        text: Font;
        description: Font;
        radius: number;
        height: number;
      };
      medium: {
        label: Font;
        text: Font;
        description: Font;
        radius: number;
        height: number;
      };
      small: {
        label: Font;
        text: Font;
        description: Font;
        radius: number;
        height: number;
      };
    };
    colors: {
      font: IColorsSet;
      background: IColorsSet;
      border: IColorsSet;
    };
  };
  search: {
    sizes: {
      small: {
        height: number;
        text: Font;
        close: number;
      };
      medium: {
        height: number;
        text: Font;
        close: number;
      };
      large: {
        height: number;
        text: Font;
        close: number;
      };
    };
    colors: {
      text: IColorsSet;
      background: IColorsSet;
      border: IColorsSet;
      icon: IColorsSet;
    };
  };
  table: {
    header: Font;
    colors: {
      header: IColorsSet;
      border: IColorsSet;
    };
  };
  checkboxes: {
    sizes: {
      large: {
        height: number;
        width: number;
        radius: number;
        icon: {
          height: number;
        };
      };
      default: {
        height: number;
        width: number;
        radius: number;
        icon: {
          height: number;
        };
      };
      medium: {
        height: number;
        width: number;
        radius: number;
        icon: {
          height: number;
        };
      };
      small: {
        height: number;
        width: number;
        radius: number;
        icon: {
          height: number;
        };
      };
    };
    states: {
      primary: {
        colors: IColorsSet;
      };
      secondary: {
        colors: IColorsSet;
      };
      neutral: {
        colors: IColorsSet;
      };
      tertiary: {
        colors: IColorsSet;
      };
    };
  };
  linearProgress: {
    sizes: {
      large: {
        height: number;
        radius: number;
      };
      default: {
        height: number;
        radius: number;
      };
      small: {
        height: number;
        radius: number;
      };
    };
    roles: {
      primary: {
        colors: IColorsSet;
      };
      secondary: {
        colors: IColorsSet;
      };
      tertiary: {
        colors: IColorsSet;
      };
    };
  };
  radios: {
    states: {
      colors: IColorsSet;
    };
  };
  toggle: {
    thumb: {
      colors: IColorsSet;
    };
    track: {
      colors: IColorsSet;
    };
  };
  tags: {
    roles: {
      neutral: {
        colors: IColorsSet;
      };
      info: {
        colors: IColorsSet;
      };
      success: {
        colors: IColorsSet;
      };
      error: {
        colors: IColorsSet;
      };
      warning: {
        colors: IColorsSet;
      };
    };
  };
  parts: {
    navbar: {
      top: {
        sizes: {
          height: number;
        };
      };
      bottom: {
        sizes: {
          height: number;
        };
      };
    };
  };
  custom?: any;
}

const Theme: ITheme = Object.freeze<ITheme>({
  sizes: {
    baseline: 4,
    spacing: {
      s_0: 0,
      s_1: 4,
      s_2: 8,
      s_3: 12,
      s_4: 16,
      s_5: 20,
      s_6: 24,
      s_7: 28,
      s_8: 32,
      s_9: 36,
      s_10: 40,
    },
    desktop: {
      breakpoint: {
        min: DESKTOP_SIZE_BREAKPOINT_MIN,
      },
    },
    tablet: {
      breakpoint: {
        min: TABLET_SIZE_BREAKPOINT_MIN,
        max: TABLET_SIZE_BREAKPOINT_MAX,
      },
    },
    mobile: {
      breakpoint: {
        max: MOBILE_SIZE_BREAKPOINT_MAX,
      },
    },
  },
  fonts: {
    semantics: {
      h1: { sizes: { ...H1_DIMENSIONS }, heights: { ...H1_LINE_HEIGHTS } },
      h2: { sizes: { ...H2_DIMENSIONS }, heights: { ...H2_LINE_HEIGHTS } },
      h3: { sizes: { ...H3_DIMENSIONS }, heights: { ...H3_LINE_HEIGHTS } },
      h4: { sizes: { ...H4_DIMENSIONS }, heights: { ...H4_LINE_HEIGHTS } },
      h5: { sizes: { ...H5_DIMENSIONS }, heights: { ...H5_LINE_HEIGHTS } },
      h6: { sizes: { ...H6_DIMENSIONS }, heights: { ...H6_LINE_HEIGHTS } },
      p: {
        sizes: {
          ...P_SIZES,
          large: {
            ...P_LARGE_DIMENSIONS,
          },
        },
        heights: {
          ...P_LINE_HEIGHTS,
          large: {
            ...P_LARGE_LINE_HEIGHTS,
          },
        },
      },
      span: { sizes: { ...P_SIZES }, heights: { ...P_LINE_HEIGHTS } },
    },
    roles: {
      primary: {
        colors: { default: "neutral.n_1" },
      },
      secondary: {
        colors: { default: "neutral.n_3" },
      },
      tertiary: {
        colors: { default: "neutral.n_4" },
      },
      inverted: {
        colors: { default: "neutral.white" },
      },
      base: {
        colors: { default: "neutral.black" },
      },
    },
  },
  buttons: {
    sizes: {
      large: {
        padding: {
          vertical: 14,
          horizontal: 24,
        },
        font: {
          size: P_SIZES.large,
          height: P_LINE_HEIGHTS.large,
          weight: "bold",
        },
        radius: 15,
        chip: {
          padding: {
            vertical: 4,
            horizontal: 20,
          },
          radius: 25,
        },
      },
      semi_large: {
        padding: {
          vertical: 12,
          horizontal: 24,
        },
        font: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "bold",
        },
        radius: 12,
        chip: {
          padding: {
            vertical: 3,
            horizontal: 19,
          },
          radius: 25,
        },
      },
      default: {
        padding: {
          vertical: 9,
          horizontal: 20,
        },
        font: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "bold",
        },
        radius: 12,
        chip: {
          padding: {
            vertical: 3,
            horizontal: 18,
          },
          radius: 25,
        },
      },
      medium: {
        padding: {
          vertical: 8,
          horizontal: 18,
        },
        font: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "medium",
        },
        radius: 10,
        chip: {
          padding: {
            vertical: 2,
            horizontal: 16,
          },
          radius: 25,
        },
      },
      small: {
        padding: {
          vertical: 6,
          horizontal: 16,
        },
        font: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "medium",
        },
        radius: 10,
        chip: {
          padding: {
            vertical: 2,
            horizontal: 12,
          },
          radius: 25,
        },
      },
    },
    roles: {
      primary: {
        colors: {
          default: "actions.primary.default",
          hover: "actions.primary.hover",
          active: "actions.primary.active",
          disabled: "actions.primary.disabled",
          inverted: "actions.primary.inverted",
          visited: "actions.primary.visited",
        },
      },
      secondary: {
        colors: {
          default: "actions.secondary.default",
          hover: "actions.secondary.hover",
          active: "actions.secondary.active",
          disabled: "actions.secondary.disabled",
          inverted: "actions.secondary.inverted",
          visited: "actions.secondary.visited",
        },
      },
      neutral: {
        colors: {
          default: "actions.neutral.default",
          hover: "actions.neutral.hover",
          active: "actions.neutral.active",
          disabled: "actions.neutral.disabled",
          inverted: "actions.neutral.inverted",
          visited: "actions.neutral.visited",
        },
      },
      tertiary: {
        colors: {
          default: "actions.tertiary.default",
          hover: "actions.tertiary.hover",
          active: "actions.tertiary.active",
          disabled: "actions.tertiary.disabled",
          inverted: "actions.tertiary.inverted",
          visited: "actions.tertiary.visited",
        },
      },
    },
  },
  links: {
    sizes: {
      large: {
        font: {
          size: P_SIZES.large,
          weight: "bold",
          height: P_LINE_HEIGHTS.large,
        },
      },
      default: {
        font: {
          size: P_SIZES.default,
          weight: "bold",
          height: P_LINE_HEIGHTS.default,
        },
      },
      small: {
        font: {
          size: P_SIZES.small,
          weight: "bold",
          height: P_LINE_HEIGHTS.small,
        },
      },
      extra_small: {
        font: {
          size: P_SIZES.extra_small,
          weight: "light",
          height: P_LINE_HEIGHTS.extra_small,
        },
      },
    },
    roles: {
      primary: {
        colors: {
          default: "actions.primary.default",
          hover: "actions.primary.hover",
          active: "actions.primary.active",
          disabled: "actions.primary.disabled",
          inverted: "actions.primary.inverted",
          visited: "actions.primary.visited",
        },
      },
      secondary: {
        colors: {
          default: "actions.secondary.default",
          hover: "actions.secondary.hover",
          active: "actions.secondary.active",
          disabled: "actions.secondary.disabled",
          inverted: "actions.secondary.inverted",
          visited: "actions.secondary.visited",
        },
      },
      neutral: {
        colors: {
          default: "actions.neutral.default",
          hover: "actions.neutral.hover",
          active: "actions.neutral.active",
          disabled: "actions.neutral.disabled",
          inverted: "actions.neutral.inverted",
          visited: "actions.neutral.visited",
        },
      },
      tertiary: {
        colors: {
          default: "actions.tertiary.default",
          hover: "actions.tertiary.hover",
          active: "actions.tertiary.active",
          disabled: "actions.tertiary.disabled",
          inverted: "actions.tertiary.inverted",
          visited: "actions.tertiary.visited",
        },
      },
    },
  },
  slider: {
    text: {
      size: P_SIZES.small,
      height: P_LINE_HEIGHTS.small,
      weight: "regular",
    },
    colors: {
      text: {
        default: "neutral.n_3",
      },
      content: {
        default: "neutral.n_8",
        active: "primary.default",
      },
    },
  },
  inputs: {
    sizes: {
      large: {
        label: {
          size: P_SIZES.large,
          height: P_LINE_HEIGHTS.large,
          weight: "heavy",
        },
        text: {
          size: P_SIZES.large,
          height: P_LINE_HEIGHTS.large,
          weight: "bold",
        },
        description: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "regular",
        },
        height: 56,
        radius: 15,
      },
      semi_large: {
        label: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "heavy",
        },
        text: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "bold",
        },
        description: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "regular",
        },
        height: 48,
        radius: 12,
      },
      default: {
        label: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "heavy",
        },
        text: {
          size: P_SIZES.default,
          height: P_LINE_HEIGHTS.default,
          weight: "bold",
        },
        description: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "regular",
        },
        height: 42,
        radius: 12,
      },
      medium: {
        label: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "heavy",
        },
        text: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "bold",
        },
        description: {
          size: P_SIZES.extra_small,
          height: P_LINE_HEIGHTS.extra_small,
          weight: "regular",
        },
        height: 38,
        radius: 10,
      },
      small: {
        label: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "heavy",
        },
        text: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "bold",
        },
        description: {
          size: P_SIZES.extra_small,
          height: P_LINE_HEIGHTS.extra_small,
          weight: "regular",
        },
        height: 32,
        radius: 10,
      },
    },
    colors: {
      font: {
        default: "neutral.n_3",
        disabled: "neutral.n_5",
        error: "states.error",
      },
      background: {
        default: "action.primary.inverted",
        disabled: "neutral.n_10",
      },
      border: {
        default: "neutral.n_5",
        active: "actions.primary.active",
      },
    },
  },
  search: {
    sizes: {
      small: {
        height: 36,
        text: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "regular",
        },
        close: 16,
      },
      medium: {
        height: 44,
        text: {
          size: P_SIZES.small,
          height: P_LINE_HEIGHTS.small,
          weight: "regular",
        },
        close: 16,
      },
      large: {
        height: 68,
        text: {
          size: P_LARGE_DIMENSIONS.desktop,
          height: P_LARGE_LINE_HEIGHTS.desktop,
          weight: "regular",
        },
        close: 24,
      },
    },
    colors: {
      text: {
        default: "neutral.n_1",
      },
      background: {
        default: "neutral.n_9",
      },
      border: {
        default: "neutral.n_9",
        active: "actions.primary.active",
      },
      icon: {
        default: "neutral.n_5",
      },
    },
  },
  table: {
    header: {
      size: P_SIZES.default,
      height: P_LINE_HEIGHTS.default,
      weight: "regular",
    },
    colors: {
      header: {
        default: "neutral.black",
      },
      border: {
        default: "neutral.n_8",
      },
    },
  },
  checkboxes: {
    sizes: {
      large: {
        radius: 8,
        height: 38,
        width: 38,
        icon: {
          height: 16,
        },
      },
      default: {
        radius: 8,
        height: 34,
        width: 34,
        icon: {
          height: 14,
        },
      },
      medium: {
        radius: 8,
        height: 26,
        width: 26,
        icon: {
          height: 10,
        },
      },
      small: {
        radius: 5,
        height: 19,
        width: 19,
        icon: {
          height: 8,
        },
      },
    },
    states: {
      primary: {
        colors: {
          default: "actions.primary.default",
          hover: "actions.primary.hover",
          active: "actions.primary.active",
          disabled: "actions.primary.disabled",
          inverted: "actions.primary.inverted",
          visited: "actions.primary.visited",
        },
      },
      secondary: {
        colors: {
          default: "actions.secondary.default",
          hover: "actions.secondary.hover",
          active: "actions.secondary.active",
          disabled: "actions.secondary.disabled",
          inverted: "actions.secondary.inverted",
          visited: "actions.secondary.visited",
        },
      },
      neutral: {
        colors: {
          default: "actions.neutral.default",
          hover: "actions.neutral.hover",
          active: "actions.neutral.active",
          disabled: "actions.neutral.disabled",
          inverted: "actions.neutral.inverted",
          visited: "actions.neutral.visited",
        },
      },
      tertiary: {
        colors: {
          default: "actions.tertiary.default",
          hover: "actions.tertiary.hover",
          active: "actions.tertiary.active",
          disabled: "actions.tertiary.disabled",
          inverted: "actions.tertiary.inverted",
          visited: "actions.tertiary.visited",
        },
      },
    },
  },
  linearProgress: {
    sizes: {
      large: {
        height: 23,
        radius: 15,
      },
      default: {
        height: 12,
        radius: 30,
      },
      small: {
        height: 6,
        radius: 30,
      },
    },
    roles: {
      primary: {
        colors: {
          default: "actions.primary.default",
        },
      },
      secondary: {
        colors: {
          default: "actions.secondary.default",
        },
      },
      tertiary: {
        colors: {
          default: "actions.tertiary.default",
        },
      },
    },
  },
  radios: {
    states: {
      colors: {
        default: "actions.neutral.shallow",
        disabled: "actions.primary.disabled",
        active: "actions.primary.default",
        hover: "actions.primary.hover",
      },
    },
  },
  toggle: {
    thumb: {
      colors: {
        default: "actions.neutral.default",
        active: "actions.primary.default",
      },
    },
    track: {
      colors: {
        default: "actions.neutral.active.p_20",
        active: "actions.primary.active.p_20",
      },
    },
  },
  tags: {
    roles: {
      success: {
        colors: {
          default: "states.success",
          inverted: "states.success.inverted",
          active: "accents.a_4.active",
        },
      },
      error: {
        colors: {
          default: "states.error",
          inverted: "states.error.inverted",
          active: "actions.secondary.active",
        },
      },
      warning: {
        colors: {
          default: "states.warning",
          inverted: "states.warning.inverted",
          active: "accents.a_5.active",
        },
      },
      info: {
        colors: {
          default: "states.info",
          inverted: "states.info.inverted",
          active: "accents.a_2.active",
        },
      },
      neutral: {
        colors: {
          default: "neutral.n_3",
          inverted: "neutral.n_8",
          active: "neutral.n_6",
        },
      },
    },
  },
  parts: {
    navbar: {
      top: {
        sizes: {
          height: 52,
        },
      },
      bottom: {
        sizes: {
          height: 52,
        },
      },
    },
  },
});

export { Theme };
export type {
  ITheme,
  IThemeButtonRole,
  IThemeButtonSize,
  ScaledSize,
  Font,
  IColorsSet,
};
